import type { AIFeedback } from '@recipes/contracts';
import { Button, Textarea } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { toast } from '@/components/feedback/toaster';

/** Thumbs up/down with an optional correction reason (spec §7.6). */
export function FeedbackButtons({
  generationId,
  initial,
}: {
  generationId: string;
  initial: AIFeedback | null;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState<AIFeedback | null>(initial);
  const [reason, setReason] = useState('');
  const [showReason, setShowReason] = useState(false);
  const mutation = useMutation({
    mutationFn: (body: { feedback: AIFeedback; reason?: string }) =>
      api.ai.feedback(generationId, body),
    onSuccess: (_, body) => {
      setValue(body.feedback);
      setShowReason(false);
      toast.success(t('ai.feedback.thanks'));
    },
    onError: (e) => toastError(e),
  });

  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      role="group"
      aria-label={t('ai.feedback.title')}
    >
      <p className="text-sm font-medium">{t('ai.feedback.title')}</p>
      <p className="text-xs text-muted-foreground">{t('ai.feedback.privacy')}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={value === 'UP' ? 'default' : 'outline'}
          size="sm"
          aria-pressed={value === 'UP'}
          loading={mutation.isPending && mutation.variables?.feedback === 'UP'}
          onClick={() => mutation.mutate({ feedback: 'UP' })}
        >
          <ThumbsUp aria-hidden /> {t('ai.feedback.up')}
        </Button>
        <Button
          type="button"
          variant={value === 'DOWN' ? 'default' : 'outline'}
          size="sm"
          aria-pressed={value === 'DOWN'}
          aria-expanded={showReason}
          onClick={() => setShowReason((s) => !s)}
        >
          <ThumbsDown aria-hidden /> {t('ai.feedback.down')}
        </Button>
      </div>
      {showReason && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate({ feedback: 'DOWN', reason: reason.trim() || undefined });
          }}
        >
          <label className="block text-sm" htmlFor="ai-feedback-reason">
            {t('ai.feedback.reason')}
          </label>
          <Textarea
            id="ai-feedback-reason"
            rows={2}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button type="submit" size="sm" loading={mutation.isPending}>
            {t('ai.feedback.send')}
          </Button>
        </form>
      )}
    </div>
  );
}
